import { Injectable, ElementRef } from '@angular/core';
import { SsrService } from './ssr.service';

@Injectable({
  providedIn: 'root',
})
export class SmoothScrollingService {

  constructor(private ssr: SsrService) {}

  public scrollToFirstInvalidControl(
    scrollContainer: ElementRef,
    errorContainers: string
  ) {
    if (this.ssr.isPlatformBrowser()) {
      setTimeout(() => {
        const formRoot = scrollContainer.nativeElement;
        const invalidElements = formRoot.querySelectorAll(errorContainers);

        if (invalidElements.length > 0) {
          this.smoothScrollToElement(invalidElements[0] as HTMLElement);
        }
      });
    }
  }

  private smoothScrollToElement(element: HTMLElement) {
    const scrollContainer = this.findScrollableContainer(element) || window;

    const { targetPosition, startPosition } = this.calculateScrollPositions(
      element,
      scrollContainer
    );

    this.animateScroll(scrollContainer, targetPosition, startPosition, () => {
      element.focus({ preventScroll: true });
    });
  }

  private findScrollableContainer(element: HTMLElement): HTMLElement | null {
    const dialogContainer = element.closest('mat-dialog-content');
    if (dialogContainer) return dialogContainer as HTMLElement;

    let parent = element.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private calculateScrollPositions(
    element: HTMLElement,
    scrollContainer: HTMLElement | Window
  ) {
    const elementRect = element.getBoundingClientRect();
    const isWindow = scrollContainer === window;

    if (isWindow) {
      return {
        targetPosition:
          elementRect.top +
          window.pageYOffset -
          window.innerHeight / 2 +
          elementRect.height / 2,
        startPosition: window.pageYOffset,
      };
    } else {
      const container = scrollContainer as HTMLElement;
      const containerRect = container.getBoundingClientRect();
      return {
        targetPosition:
          elementRect.top - containerRect.top + container.scrollTop - 20,
        startPosition: container.scrollTop,
      };
    }
  }

  private animateScroll(
    scrollContainer: HTMLElement | Window,
    targetPosition: number,
    startPosition: number,
    onComplete: () => void
  ) {
    const duration = 300;
    let startTime: number;

    const scrollStep = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);

      const easing =
        percentage < 0.5
          ? 2 * percentage * percentage
          : 1 - Math.pow(-2 * percentage + 2, 2) / 2;

      if (scrollContainer === window) {
        window.scrollTo(
          0,
          startPosition + (targetPosition - startPosition) * easing
        );
      } else {
        (scrollContainer as HTMLElement).scrollTop =
          startPosition + (targetPosition - startPosition) * easing;
      }

      if (progress < duration) {
        window.requestAnimationFrame(scrollStep);
      } else {
        onComplete();
      }
    };

    window.requestAnimationFrame(scrollStep);
  }
}
